import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, ShieldCheck, UserCog, X } from 'lucide-react';
import api from '../api/client';
import { clearSelectedAppId, clearToken, getCurrentUser, getIsPortalAdmin } from '../utils/storage';
import ENTITY_ICONS from './common/entityIcons';
import AppSwitcher from './common/AppSwitcher';
import AccountMenu from './AccountMenu';
import { lockScroll, unlockScroll } from './common/Modal';
import { useApps } from '../contexts/AppsContext';
import { NoticeProvider } from './common/Notices';
import { ConfirmProvider } from './common/ConfirmDialog';
import { cn } from '../utils/cn';

// portalAdminOnly items are install-wide/cross-app (aggregate dashboard) and match
// the backend portalAdminRole gate — hidden from app admins. Apps stays visible: app
// admins manage their OWN app(s) there (the list is UAM-scoped, global actions gated).
const navSections = [
    {
        id: 'overview',
        label: 'Overview',
        items: [
            { to: '/dashboard', label: 'Dashboard', icon: ENTITY_ICONS.dashboard, portalAdminOnly: true },
            { to: '/portal-admins', label: 'Portal admins', icon: ENTITY_ICONS.portalAdmins, portalAdminOnly: true },
        ],
    },
    { id: 'applications', label: 'Applications', items: [{ to: '/apps', label: 'Apps', icon: ENTITY_ICONS.apps }] },
    {
        id: 'directory',
        label: 'Directory',
        items: [
            { to: '/users', label: 'Users', icon: ENTITY_ICONS.users },
            { to: '/groups', label: 'Groups', icon: ENTITY_ICONS.groups },
            { to: '/tenants', label: 'Tenants', icon: ENTITY_ICONS.tenants },
            { to: '/invitations', label: 'Invitations', icon: ENTITY_ICONS.invitations },
        ],
    },
    {
        id: 'access',
        label: 'Access control',
        items: [
            { to: '/roles', label: 'Roles', icon: ENTITY_ICONS.roles },
            { to: '/components', label: 'Components', icon: ENTITY_ICONS.components },
            { to: '/delegations', label: 'Delegations', icon: ENTITY_ICONS.delegations },
        ],
    },
    {
        id: 'authentication',
        label: 'Authentication',
        items: [
            { to: '/sso', label: 'SSO', icon: ENTITY_ICONS.sso },
            { to: '/api-keys', label: 'API keys', icon: ENTITY_ICONS.apiKeys },
        ],
    },
    {
        id: 'integrations',
        label: 'Integrations',
        items: [{ to: '/webhooks', label: 'Webhooks', icon: ENTITY_ICONS.webhooks }],
    },
    {
        id: 'monitoring',
        label: 'Monitoring',
        items: [{ to: '/audit-trail', label: 'Audit trail', icon: ENTITY_ICONS.auditTrail }],
    },
];

// Routes that span every app (or are about the signed-in person): the top bar shows
// "All apps" instead of the app switcher, so scope is always explicit.
const isCrossAppRoute = (pathname) =>
    pathname === '/dashboard' ||
    pathname === '/portal-admins' ||
    pathname === '/apps' || pathname === '/account' || pathname.startsWith('/account/');

const DESKTOP_QUERY = '(min-width: 768px)';

const navLinkClass = ({ isActive }) =>
    cn(
        'flex min-h-11 items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition md:min-h-0 md:py-1',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200',
        isActive ? 'bg-primary-50 text-primary-700' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
    );

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

const useIsDesktop = () => {
    const get = () =>
        typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DESKTOP_QUERY).matches : true;
    const [isDesktop, setIsDesktop] = useState(get);
    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mql = window.matchMedia(DESKTOP_QUERY);
        const onChange = () => setIsDesktop(mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    return isDesktop;
};

const BrandMark = () => (
    <span className="grid h-8 w-8 shrink-0 place-content-center rounded-lg bg-primary-600 text-white">
        <ShieldCheck className="h-4 w-4" aria-hidden="true" />
    </span>
);

const ShellLayout = () => {
    const currentUser = getCurrentUser();
    const isPortalAdmin = getIsPortalAdmin();
    const location = useLocation();
    const navigate = useNavigate();
    const { apps, ensureAppsLoaded, resetApps } = useApps();
    const isDesktop = useIsDesktop();
    const hasEnsuredRef = useRef(false);
    const menuButtonRef = useRef(null);
    const drawerRef = useRef(null);
    const navRef = useRef(null);
    const mainRef = useRef(null);
    const focusAfterCloseRef = useRef(null); // 'button' | 'main' | null
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [signingOut, setSigningOut] = useState(false);
    const [navOverflows, setNavOverflows] = useState(false);

    const drawerOpen = sidebarOpen && !isDesktop;
    const crossApp = isCrossAppRoute(location.pathname);
    const homePath = isPortalAdmin ? '/dashboard' : '/apps';

    const sessionApp = currentUser?.appId ? apps.find((a) => a.app_id === currentUser.appId) : null;
    const sessionLabel = currentUser?.appId
        ? [sessionApp?.app_name || currentUser.appId, currentUser.tenantName].filter(Boolean).join(' · ')
        : '';

    const sections = navSections
        .map((s) => ({ ...s, items: s.items.filter((item) => !item.portalAdminOnly || isPortalAdmin) }))
        .filter((s) => s.items.length > 0);

    // Tab title follows the page: "<Page> · User Management". Restored when the shell unmounts
    // (sign-out), so the login page shows the plain app name.
    const pageTitle = location.pathname.startsWith('/account')
        ? 'Account'
        : navSections
              .flatMap((s) => s.items)
              .find((i) => location.pathname === i.to || location.pathname.startsWith(`${i.to}/`))?.label;
    useEffect(() => {
        document.title = pageTitle ? `${pageTitle} · User Management` : 'User Management';
    }, [pageTitle]);
    useEffect(
        () => () => {
            document.title = 'User Management';
        },
        []
    );

    // Ensure apps are loaded when entering protected routes (e.g., after login)
    // Only run once when ShellLayout first mounts
    useEffect(() => {
        if (!hasEnsuredRef.current) {
            hasEnsuredRef.current = true;
            ensureAppsLoaded();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeDrawer = useCallback((focusTarget = 'button') => {
        focusAfterCloseRef.current = focusTarget;
        setSidebarOpen(false);
    }, []);

    // Route change (e.g. back/forward) closes the drawer.
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    // Growing to desktop closes the drawer (the sidebar is always shown there).
    useEffect(() => {
        if (isDesktop) setSidebarOpen(false);
    }, [isDesktop]);

    // Drawer open: lock page scroll, move focus in, trap Tab, Escape closes.
    useEffect(() => {
        if (!drawerOpen) return undefined;
        lockScroll();
        const drawer = drawerRef.current;
        // After the commit that drops `inert`/`invisible`; not rAF, which stalls in background tabs.
        const focusTimer = setTimeout(() => {
            (drawer?.querySelector('nav a[href]') || drawer?.querySelector(FOCUSABLE))?.focus();
        }, 0);
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeDrawer('button');
                return;
            }
            if (e.key !== 'Tab' || !drawer) return;
            const nodes = Array.from(drawer.querySelectorAll(FOCUSABLE));
            if (!nodes.length) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (e.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
                e.preventDefault();
                first.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => {
            clearTimeout(focusTimer);
            window.removeEventListener('keydown', onKey);
            unlockScroll();
        };
    }, [drawerOpen, closeDrawer]);

    // After the drawer closes (and the rest of the page is no longer inert), put focus
    // back: on the menu button for X / overlay / Escape, on the page for navigation.
    useEffect(() => {
        if (sidebarOpen || !focusAfterCloseRef.current) return;
        const target = focusAfterCloseRef.current;
        focusAfterCloseRef.current = null;
        requestAnimationFrame(() => {
            if (target === 'main') mainRef.current?.focus({ preventScroll: true });
            else menuButtonRef.current?.focus();
        });
    }, [sidebarOpen]);

    // Show a fade at the bottom of the nav when there are more items below the fold.
    useEffect(() => {
        const nav = navRef.current;
        if (!nav) return undefined;
        const update = () => setNavOverflows(nav.scrollHeight - nav.scrollTop - nav.clientHeight > 1);
        update();
        nav.addEventListener('scroll', update, { passive: true });
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        ro?.observe(nav);
        return () => {
            nav.removeEventListener('scroll', update);
            ro?.disconnect();
        };
    }, [sections.length]);

    // Sign out: end the server session (best effort — the local sign-out must happen
    // even if the API is unreachable), then forget everything this browser holds.
    const handleSignOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        try {
            await api.post('/api/auth/logout', null, { timeout: 8000 });
        } catch {
            // The token is cleared below either way; the server session expires on its own.
        }
        clearToken();
        clearSelectedAppId();
        resetApps();
        navigate('/login', { replace: true, state: { signedOut: true } });
    };

    const topOffset = currentUser?.actingAs
        ? 'md:top-24 md:h-[calc(100dvh-6rem)]'
        : 'md:top-14 md:h-[calc(100dvh-3.5rem)]';

    return (
        <NoticeProvider>
            <ConfirmProvider>
                <a
                    href="#main-content"
                    onClick={(e) => {
                        // Focus the page without a hash change (the router owns the URL).
                        e.preventDefault();
                        mainRef.current?.focus();
                    }}
                    className="sr-only rounded-md bg-white px-3 py-2 text-sm font-medium text-primary-700 shadow-lg focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-[70] focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                    Skip to content
                </a>

                <div className="sticky top-0 z-30" inert={drawerOpen}>
                    {currentUser?.actingAs && (
                        <div
                            role="status"
                            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 md:h-10 md:flex-nowrap md:py-0"
                        >
                            <UserCog className="h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
                            <p className="min-w-0 flex-1 md:truncate">
                                You’re acting as <span className="font-semibold">{currentUser.username}</span>. Changes
                                are made as this user.
                            </p>
                            <button
                                type="button"
                                onClick={handleSignOut}
                                disabled={signingOut}
                                className="min-h-11 shrink-0 rounded-md border border-amber-300 bg-white px-3 text-sm font-medium text-amber-900 transition hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 disabled:cursor-wait disabled:opacity-60 md:min-h-0 md:py-1"
                            >
                                {signingOut ? 'Signing out…' : 'End session (signs you out)'}
                            </button>
                        </div>
                    )}

                    <header className="flex h-14 items-center gap-2 border-b border-slate-200 bg-white px-2 sm:gap-3 sm:px-4">
                        <button
                            ref={menuButtonRef}
                            type="button"
                            onClick={() => setSidebarOpen(true)}
                            aria-label="Open menu"
                            aria-controls="app-sidebar"
                            aria-expanded={sidebarOpen}
                            className="grid h-11 w-11 shrink-0 place-content-center rounded-lg text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 md:hidden"
                        >
                            <Menu className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <Link
                            to={homePath}
                            className="flex shrink-0 items-center gap-2.5 rounded-lg p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 md:w-52"
                        >
                            <BrandMark />
                            <span className="hidden text-sm font-semibold text-slate-900 sm:inline">
                                User Management
                            </span>
                            <span className="sr-only sm:hidden">User Management home</span>
                        </Link>
                        <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-3">
                            {crossApp ? (
                                <span className="min-w-0 truncate text-sm font-medium text-fg-secondary">All apps</span>
                            ) : (
                                <AppSwitcher className="min-w-0 max-w-[11rem] sm:max-w-none" />
                            )}
                            <span className="h-6 w-px shrink-0 bg-line" aria-hidden="true" />
                            <AccountMenu
                                user={currentUser}
                                sessionLabel={sessionLabel}
                                onSignOut={handleSignOut}
                                signingOut={signingOut}
                            />
                        </div>
                    </header>
                </div>

                <div className="md:flex">
                    {drawerOpen && (
                        <div
                            className="fixed inset-0 z-40 bg-slate-900/40"
                            onClick={() => closeDrawer('button')}
                            aria-hidden="true"
                        />
                    )}

                    <aside
                        ref={drawerRef}
                        id="app-sidebar"
                        aria-label="Primary"
                        inert={!isDesktop && !sidebarOpen}
                        className={cn(
                            'fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] shrink-0 flex-col border-r border-slate-200 bg-white shadow-xl duration-300',
                            'md:sticky md:bottom-auto md:z-auto md:w-60 md:translate-x-0 md:shadow-none',
                            topOffset,
                            // Opening: visible at once (so focus can move in); closing: visibility waits for the slide.
                            sidebarOpen
                                ? 'visible translate-x-0 transition-transform'
                                : 'invisible -translate-x-full transition-[transform,visibility] md:visible'
                        )}
                    >
                        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-1.5 md:hidden">
                            <span className="flex items-center gap-2.5 px-1">
                                <BrandMark />
                                <span className="text-sm font-semibold text-slate-900">User Management</span>
                            </span>
                            <button
                                type="button"
                                onClick={() => closeDrawer('button')}
                                aria-label="Close menu"
                                className="grid h-11 w-11 place-content-center rounded-lg text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
                            >
                                <X className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>

                        <div className="relative min-h-0 flex-1">
                            <nav
                                ref={navRef}
                                aria-label="Admin sections"
                                className="h-full space-y-2.5 overflow-y-auto px-2.5 py-3"
                            >
                                {sections.map((section) => (
                                    <div key={section.id}>
                                        <p
                                            id={`nav-section-${section.id}`}
                                            className="px-2.5 pb-1 text-xs font-medium leading-4 text-slate-600"
                                        >
                                            {section.label}
                                        </p>
                                        <ul aria-labelledby={`nav-section-${section.id}`} className="space-y-0.5">
                                            {section.items.map((item) => (
                                                <li key={item.to}>
                                                    <NavLink
                                                        to={item.to}
                                                        className={navLinkClass}
                                                        onClick={() => {
                                                            if (drawerOpen) closeDrawer('main');
                                                        }}
                                                    >
                                                        <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                                        {item.label}
                                                    </NavLink>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </nav>
                            {navOverflows && (
                                <div
                                    className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent"
                                    aria-hidden="true"
                                />
                            )}
                        </div>
                    </aside>

                    <main
                        ref={mainRef}
                        id="main-content"
                        tabIndex={-1}
                        inert={drawerOpen}
                        className="min-w-0 flex-1 p-4 focus:outline-none md:p-6"
                    >
                        <Outlet />
                    </main>
                </div>
            </ConfirmProvider>
        </NoticeProvider>
    );
};

export default ShellLayout;
